import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take, finalize } from 'rxjs/operators';
import { random, cloneDeep, merge, without } from 'lodash';
import { DecimalPipe } from '@angular/common';
import { DatePipe } from '@angular/common';
import { Chart } from 'chart.js';
import * as ChartDataLabels from 'chartjs-plugin-datalabels';

import { AppService } from '../../../shared/services/app.service';
import { OfferService } from '../../services/offer.service';
import { Offer } from '../../../shared/models/offer';
import { TranslateService } from '@ngx-translate/core';
import { FileService } from '~common/app/shared/services/file.service';
import { settings } from "~common/app/app.settings";
import { granularityForRange } from 'app/shared/utils/dates';

// Offer types whose performance mirrors a Performances tab, scoped to the offer. The others
// keep the legacy views.metrics rendering below.
const DASHBOARD_TAB_BY_OFFER_TYPE = {
  CASHBACK: 'REWARDS',
  VOUCHER: 'VOUCHERS'
};

Chart.defaults.global.plugins.datalabels.display = true;

Chart.pluginService.register({
  afterDraw: function (chart: any) {
    let lines = chart.config.options.lineAt;

    if (typeof lines !== 'undefined') {

      if (!Array.isArray(lines)) {
        lines = [lines];
      }

      lines.forEach((line) => {
        let lineAt = line.value;
        const ctxPlugin = chart.chart.ctx;
        const xAxe = chart.scales[chart.config.options.scales.xAxes[0].id];
        const yAxe = chart.scales[chart.config.options.scales.yAxes[0].id];

        if (yAxe.min !== 0) {
          return;
        }

        ctxPlugin.strokeStyle = line.borderColor || 'black';
        ctxPlugin.beginPath();
        ctxPlugin.setLineDash(line.borderDash || []);
        ctxPlugin.lineWidth = 1;

        lineAt = (lineAt - yAxe.min) * (100 / yAxe.max);
        lineAt = (100 - lineAt) / 100 * (yAxe.height) + yAxe.top;

        ctxPlugin.moveTo(xAxe.left, lineAt);
        ctxPlugin.lineTo(xAxe.right, lineAt);
        ctxPlugin.stroke();
        ctxPlugin.setLineDash([]);
      });
    }
  }
});

@Component({
  selector: 'app-offer-metrics-page',
  templateUrl: './offer-metrics-page.component.html',
  styleUrls: ['./offer-metrics-page.component.sass'],
  providers: [DecimalPipe, DatePipe]
})
export class OfferMetricsPageComponent implements OnInit {
  offer: Offer = new Offer();
  metrics: any;
  loading: boolean;
  loadingMetrics: boolean;
  params: any;
  id: string;
  filtersShown = false;

  // Perks-backed dashboard state, kept apart from the legacy `filters` below which means
  // something different (a single month + granularity rather than a range).
  dashboardTab: string = null;
  dashboardData: any = null;
  dashboardLoading = true;
  dashboardError = false;
  bounds = { min: null, max: null };
  dashboard = {
    granularity: 'monthly',
    dates: { start: null, end: null }
  };

  filters = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    granularity: 'monthly',
    views: {
      't0': true,
      't1': true,
      't2': true,
      't3': true,
      't4': true,
      't5': true,
    }
  };

  year = new Date().getFullYear();
  month = new Date().getMonth() + 1;
  granularity = 'monthly';
  plugins = [ChartDataLabels];

  chartOptions = {
    plugins: {
      datalabels: {
        display: false,
        align: 'end',
        anchor: 'end',
        font: {
          size: 10,
        },
        offset: 5,
        backgroundColor: 'white',
        borderColor: 'rgba(0,0,0,.2)',
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 25,
        formatter: (value, context) => {
          return this.numberPipe.transform(value) + (context.dataset.symbol || '');
        }
      }
    },
    tooltips: {
      callbacks: {
        label: (obj, data) => {
          return this.numberPipe.transform(obj.yLabel) + (data.datasets[obj.datasetIndex].symbol || '');
        }
      }
    },
    responsive: true,
    layout: {
      padding: {
        top: 25,
        bottom: 5
      }
    },
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    scales: {
      yAxes: [{
        scaleLabel: {
          paddingTop: 30
        },
        ticks: {
          beginAtZero: true,
          padding: 10,
          callback: (value) => {
            return this.numberPipe.transform(value);
          }
        }
      }]
    }
  };

  baseData = {
    datasets: [
      {
        label: null,
        fill: false,
        backgroundColor: '#5BBD72',
        borderColor: '#5BBD72',
      },
    ],
  };

  fakeData = merge(this.baseData, {
    labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'November', 'December'],
    datasets: [
      {
        data: [65, 59, 80, 81, 56, 55, 40, 81, 56, 55],
      },
    ],
  });

  totalSpentData: any = {};
  totalSpentOptions: any = {};

  commissionData: any = {};
  commissionOptions: any = {};

  cashbackData: any = {};
  cashbackOptions: any = {};

  averageSpentData: any = {};
  averageSpentOptions: any = {};

  activationsData: any = {};
  activationsOptions: any = {};

  viewsData: any = {};
  viewsDataSmall0: any = {};
  viewsDataSmall1: any = {};
  viewsDataSmall2: any = {};
  viewsDataSmall3: any = {};
  viewsDataSmall4: any = {};
  viewsDataSmall5: any = {};
  viewsOptions: any = {};
  viewsSmallOptions: any = {};
  SSOEnabled: boolean;

  constructor(
    private fileService: FileService,
    private appService: AppService,
    private offerService: OfferService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private numberPipe: DecimalPipe,
    private datePipe: DatePipe
  ) {
    this.appService.setTitle('offers.metrics.heading');
    this.params = this.route.snapshot.paramMap;
  }

  ngOnInit() {
    this.SSOEnabled = settings.app.SSOEnabled;
    this.id = this.params.get('id');

    if (this.id) {
      this.loading = true;

      this.offerService.get(this.id)
        .pipe(
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((response: any) => {
          this.offer = response;
          this.dashboardTab = DASHBOARD_TAB_BY_OFFER_TYPE[response.type];

          if (!this.dashboardTab) {
            this.loadMetrics();
            return;
          }
          this.bounds = {
            min: response.start_date ? new Date(response.start_date) : null,
            max: this.lastDayOf(response)
          };
          // app-datepicker-full emits the LIFETIME range once bounds are set, which loads the page
        });
    }
  }

  onDownload(url) {
    let downloadUrl = url;

    if (this.SSOEnabled) {
      downloadUrl = settings.api.url + url;
    }
    this.fileService.download(downloadUrl, this.offer.reference + '.csv');
  }

  onChangeViewFilter() {
    this.processViews();
  }

  // --- Perks-backed dashboard (CASHBACK / VOUCHER offers) ---

  // the chart component speaks 'day'/'month'; the API speaks 'daily'/'monthly'
  get chartGranularity(): string {
    return this.dashboard.granularity === 'daily' ? 'day' : 'month';
  }

  onDashboardDatesChange() {
    this.dashboard.granularity = granularityForRange(this.dashboard.dates.start, this.dashboard.dates.end);
    this.loadDashboard();
  }

  onDashboardExport() {
    const params = this.buildDashboardParams();
    if (!params) {
      return;
    }
    this.fileService.download(
      this.offerService.getDashboardMetricsExportUrl(this.dashboardTab, params),
      this.offer.reference + '.csv'
    );
  }

  // Today, or the offer's end date once it is behind us: an ended offer has nothing to show past
  // its last day. Keeps the wall-clock time rather than zeroing it, because the params are
  // serialised with toISOString() and a local midnight lands on the previous day east of UTC.
  private lastDayOf(offer) {
    const now = new Date();
    if (!offer.end_date) {
      return now;
    }
    const end = new Date(offer.end_date);
    end.setUTCHours(23, 59, 59, 999);
    return end < now ? end : now;
  }

  private buildDashboardParams() {
    if (!this.offer || !this.dashboardTab || !this.dashboard.dates.end) {
      return null;
    }

    const params: any = {
      end_date: this.dashboard.dates.end.toISOString().split('T')[0],
      group_by: this.dashboard.granularity,
      program_offer_id: this.offer.id
    };
    if (this.dashboard.dates.start) {
      params.start_date = this.dashboard.dates.start.toISOString().split('T')[0];
    }
    return params;
  }

  private loadDashboard() {
    const params = this.buildDashboardParams();
    if (!params) {
      return;
    }
    this.dashboardLoading = true;

    this.offerService.getDashboardMetrics(this.dashboardTab, params)
      .pipe(
        take(1),
        finalize(() => {
          this.dashboardLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.dashboardData = response;
          this.dashboardError = false;
        },
        // forkJoin fails as a whole as soon as one metric does. Dropping the previous response
        // matters more than the message: keeping it would leave the figures of the range that
        // did load sitting under the datepicker of the range that did not.
        error: () => {
          this.dashboardData = null;
          this.dashboardError = true;
        }
      });
  }

  loadMetrics() {
    this.loadingMetrics = true;

    this.offerService.getMetrics(this.id, this.granularity, this.year, this.month)
      .pipe(
        take(1),
        finalize(() => {
          this.loadingMetrics = false;
        })
      )
      .subscribe((response: any) => {
        this.metrics = response;

        this.processTotalSpentData();
        this.processCommissionData();
        this.processCashbackData();
        this.processAverageSpentData();
        this.processViews();
        this.processActivationsData();
      });
  }

  processTotalSpentData() {
    const labels = [];
    const data = [];

    this.totalSpentData = cloneDeep(this.baseData);

    Object.keys(this.metrics.periods).forEach((period) => {
      labels.push(this.getLabels(period));
      data.push(this.metrics.periods[period].total_spent);
    });

    this.totalSpentData.labels = labels;
    this.totalSpentData.datasets[0].symbol = '€';
    this.totalSpentData.datasets[0].data = data;
  }

  processCommissionData() {
    const labels = [];
    const data = [];

    this.commissionData = cloneDeep(this.baseData);

    Object.keys(this.metrics.periods).forEach((period) => {
      labels.push(this.getLabels(period));
      data.push(this.metrics.periods[period].fees);
    });
    this.commissionData.labels = labels;
    this.commissionData.datasets[0].symbol = '€';
    this.commissionData.datasets[0].data = data;
  }

  processActivationsData() {
    const labels = [];
    const data = [];

    this.activationsData = cloneDeep(this.baseData);

    Object.keys(this.metrics.periods).forEach((period) => {
      labels.push(this.getLabels(period));
      data.push(this.metrics.periods[period].activations);
    });
    this.activationsData.labels = labels;
    this.activationsData.datasets[0].symbol = '';
    this.activationsData.datasets[0].data = data;
  }

  processCashbackData() {
    const labels = [];
    const data = [];
    const radius = [];
    const rates = [];

    this.cashbackData = cloneDeep(this.baseData);
    this.cashbackOptions = cloneDeep(this.chartOptions);

    this.cashbackOptions.plugins.datalabels.display = true;
    this.cashbackOptions.plugins.datalabels.formatter = (value, context) => {
      const percent = context.dataset.rates[context.dataIndex];
      return this.numberPipe.transform(percent) + (context.dataset.symbol || '');
    };

    Object.keys(this.metrics.periods).forEach((period, i) => {
      labels.push(this.getLabels(period));
      data.push(this.metrics.periods[period].total_cashback);
      radius.push(this.metrics.periods[period].redemption_rate);
      rates.push(this.metrics.periods[period].redemption_rate);
    });

    this.cashbackData.labels = labels;
    this.cashbackData.datasets[0].symbol = '';
    this.cashbackData.datasets[0].data = data;
    this.cashbackData.datasets[0].rates = rates;
    this.cashbackData.datasets[0].pointRadius = radius;
    this.cashbackData.datasets[0].pointHoverRadius = radius;
    this.cashbackData.datasets[0].borderColor = 'transparent';
  }

  processAverageSpentData() {
    const labels = [];
    const data = [];
    const dataTop = [], dataBar = [], dataMiddle = [], dataBottom = [];
    const dataAvg = [], dataMin = [], dataMax = [];
    let averageMin = 0, averageMax = 0;

    this.averageSpentData = cloneDeep(this.baseData);
    this.averageSpentOptions = merge({}, this.chartOptions, {
      lineAt: [
        {
          value: 0,
          borderColor: 'red',
          borderWidth: 1,
          borderDash: [1, 1],
        },
        {
          value: 0,
          borderColor: 'green',
          borderWidth: 1,
          borderDash: [1, 1],
        },
      ],
      legend: {
        display: true,
        labels: {
          padding: 30,
          boxWidth: 12
        }
      },
      tooltips: {
        xAlign: 'rignt',
        mode: 'index',
        callbacks: {
          label: (tp, context) => {
            if (tp.datasetIndex === 0) {
              return null;
            }

            const entry = context.datasets[tp.datasetIndex];

            if (!entry.raw) {
              return null;
            }

            const value = entry.raw[tp.index] || 0;
            const symbol = entry.symbol || '';

            return this.numberPipe.transform(value) + symbol;
          },
        },
      },
      scales: {
        xAxes: [{
          stacked: true,
          ticks: {
            beginAtZero: true
          }
        }],
        yAxes: [{
          stacked: true,
          ticks: {
            beginAtZero: true
          }
        }]
      }
    });

    this.averageSpentData.datasets = [
      {
        // Transparent
        label: '',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
      {
        // RED
        label: 'Min spent',
        backgroundColor: '#F05352',
        borderColor: 'white',
        borderWidth: 2,
      },
      {
        // GRAY
        label: 'Average spent',
        backgroundColor: 'gray',
        borderColor: 'white',
        borderWidth: 2,
      },
      {
        // GREEN
        label: 'Max spent',
        backgroundColor: '#5BBD72',
        borderColor: 'white',
        borderWidth: 2,
      },
      {
        label: 'Average min spent',
        type: 'line',
        fill: false,
        borderColor: '#F05352',
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderDash: [1, 1],
      },
      {
        label: 'Average max spent',
        type: 'line',
        fill: false,
        borderColor: '#5BBD72',
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderDash: [1, 1],
      }
    ];

    Object.keys(this.metrics.periods).forEach((period: any) => {
      const min = this.metrics.periods[period].minimum_spent;
      const avg = this.metrics.periods[period].average_spent;
      const max = this.metrics.periods[period].maximum_spent;
      const trait = Math.ceil((max - min) / 25);

      averageMin += min;
      averageMax += max;

      labels.push(this.getLabels(period));

      dataTop.push(max - avg - trait);
      dataBar.push(trait);
      dataMiddle.push(avg - min);
      dataBottom.push(min);
      dataMin.push(min);
      dataMax.push(max);
      dataAvg.push(avg);
    });

    const periods = Object.keys(this.metrics.periods);

    averageMin = averageMin / periods.length;
    averageMax = averageMax / periods.length;

    this.averageSpentData.labels = labels;

    this.averageSpentData.datasets[3].raw = dataMax;
    this.averageSpentData.datasets[3].symbol = '€';
    this.averageSpentData.datasets[3].data = dataTop;

    this.averageSpentData.datasets[2].raw = dataAvg;
    this.averageSpentData.datasets[2].symbol = '€';
    this.averageSpentData.datasets[2].data = dataBar;

    this.averageSpentData.datasets[1].raw = dataMin;
    this.averageSpentData.datasets[1].symbol = '€';
    this.averageSpentData.datasets[1].data = dataMiddle;

    this.averageSpentData.datasets[0].raw = data;
    this.averageSpentData.datasets[0].symbol = '€';
    this.averageSpentData.datasets[0].data = dataBottom;

    this.averageSpentOptions.lineAt[0].value = averageMin;
    this.averageSpentOptions.lineAt[1].value = averageMax;
  }

  processViews() {
    const labels = [];
    const data0 = [], data1 = [], data2 = [], data3 = [], data4 = [], data5 = [];

    this.viewsData = cloneDeep(this.baseData);
    this.viewsOptions = cloneDeep(this.chartOptions);
    this.viewsSmallOptions = {

      layout: {
        margin: 0,
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        }
      },
      plugins: {
        datalabels: {
          display: false,
        }
      },
      scales: {
        xAxes: [{
          ticks: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false
          },
          gridLines: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
            color: 'transparent'
          }
        }],
        yAxes: [{
          ticks: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
          },
          gridLines: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
            color: 'transparent'
          }
        }]
      },
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
    };

    this.viewsData.datasets = [
      {
        borderWidth: 2,
        backgroundColor: '#FCF3F8',
        borderColor: '#DF8ABD',
        borderDash: [4, 4],
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
      {
        borderWidth: 2,
        backgroundColor: '#FCF3F8',
        borderColor: '#DF8ABD',
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
      {
        borderWidth: 2,
        backgroundColor: '#F2EFF6',
        borderColor: '#B7A6CF',
        borderDash: [4, 4],
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
      {
        borderWidth: 2,
        backgroundColor: '#F2EFF6',
        borderColor: '#B7A6CF',
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
      {
        borderWidth: 2,
        backgroundColor: '#ECF8FE',
        borderColor: '#16B5F9',
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
      {
        borderWidth: 2,
        backgroundColor: '#EDF9F1',
        borderColor: '#4ec873',
        fill: false,
        bezierCurve: false,
        lineTension: 0
      },
    ];

    const extra = { fill: true, pointRadius: 0 };

    this.viewsDataSmall0.datasets = [merge({}, this.viewsData.datasets[0], extra)];
    this.viewsDataSmall1.datasets = [merge({}, this.viewsData.datasets[1], extra)];
    this.viewsDataSmall2.datasets = [merge({}, this.viewsData.datasets[2], extra)];
    this.viewsDataSmall3.datasets = [merge({}, this.viewsData.datasets[3], extra)];
    this.viewsDataSmall4.datasets = [merge({}, this.viewsData.datasets[4], extra)];
    this.viewsDataSmall5.datasets = [merge({}, this.viewsData.datasets[5], extra)];

    Object.keys(this.metrics.periods).forEach((period) => {
      labels.push(this.getLabels(period));
      data0.push(this.metrics.periods[period].views_listed_total);
      data1.push(this.metrics.periods[period].views_listed_unique);
      data2.push(this.metrics.periods[period].views_detailed_total);
      data3.push(this.metrics.periods[period].views_detailed_unique);
      data4.push(this.metrics.periods[period].redemptions);
      data5.push(this.metrics.periods[period].activations);
    });

    this.viewsData.labels = labels;

    if (this.filters.views.t0) {
      this.viewsData.datasets[0].data = data0;
    }

    if (this.filters.views.t1) {
      this.viewsData.datasets[1].data = data1;
    }

    if (this.filters.views.t2) {
      this.viewsData.datasets[2].data = data2;
    }

    if (this.filters.views.t3) {
      this.viewsData.datasets[3].data = data3;
    }

    if (this.filters.views.t4) {
      this.viewsData.datasets[4].data = data4;
    }

    if (this.filters.views.t5) {
      this.viewsData.datasets[5].data = data5;
    }

    this.viewsDataSmall0.labels = labels;
    this.viewsDataSmall0.datasets[0].data = data0;

    this.viewsDataSmall1.labels = labels;
    this.viewsDataSmall1.datasets[0].data = data1;

    this.viewsDataSmall2.labels = labels;
    this.viewsDataSmall2.datasets[0].data = data2;

    this.viewsDataSmall3.labels = labels;
    this.viewsDataSmall3.datasets[0].data = data3;

    this.viewsDataSmall4.labels = labels;
    this.viewsDataSmall4.datasets[0].data = data4;

    this.viewsDataSmall5.labels = labels;
    this.viewsDataSmall5.datasets[0].data = data5;

    this.viewsData.data1 = this.sliceData(this.viewsData, 0, 2);
    this.viewsData.data2 = this.sliceData(this.viewsData, 2, 4);
    this.viewsData.data3 = this.sliceData(this.viewsData, 4, 6);
  }

  getLabels(string) {
    if (this.granularity === 'daily') {
      return this.getDayFromDate(string);
    } else {
      return this.getMonthFromDate(string);
    }
  }

  getDayFromDate(string) {
    const date = new Date(string);
    const formatted = this.datePipe.transform(date);

    return formatted;
  }

  getMonthFromDate(string) {
    const date = new Date(string);
    const month = date.getMonth();
    const i18nMonths = Object.values(this.translateService.instant('shared.date.month')).map((f: any) => f());

    return i18nMonths[month];
  }

  getEndDate() {
    if (!this.offer.end_date) {
      return new Date();
    }

    return new Date(
      Math.min(
        new Date().getTime(),
        new Date(this.offer.end_date).getTime()
      )
    );
  }

  onConfirmFilter() {
    this.filtersShown = false;
    this.year = this.filters.year;
    this.granularity = this.filters.granularity;
    this.month = this.filters.month;

    this.loadMetrics();
  }

  onCancelFilter() {
    this.filtersShown = false;
    this.filters.year = this.year;
    this.filters.granularity = this.granularity;
    this.filters.month = this.month;
  }

  onToggleFilters() {
    this.filtersShown = !this.filtersShown;
  }

  onClickGranularity() {
    if (this.filters.granularity === 'daily') {
      this.filters.granularity = 'monthly';
    } else {
      this.filters.granularity = 'daily';
    }
  }

  onPrevMonth() {
    this.filters.month--;

    if (this.filters.month === 0) {
      this.filters.month = 12;
    }
  }

  onNextMonth() {
    this.filters.month++;

    if (this.filters.month > 12) {
      this.filters.month = 1;
    }
  }

  onPrevYear() {
    this.filters.year--;
  }

  onNextYear() {
    if (this.filters.year < new Date().getFullYear()) {
      this.filters.year++;
    }
  }

  hasPrevYear() {
    return true;
    // return this.filters.year < new Date(this.offer.created_at).getFullYear();
  }

  hasNextYear() {
    return this.filters.year < new Date().getFullYear();
  }

  sliceData(object: any, start: number, end: number) {
    return {
      labels: object.labels,
      datasets: object.datasets.slice(start, end),
    };
  }
}
